import { toApiFailure } from "../../lib/errors";
import type {
  DeletionJournalEntry,
  DeletionJournalKind,
  DeletionJournalStatus,
  DsgvoDataObject,
  DsgvoOccurrenceField,
  DsgvoPerson,
  DsgvoPersonOccurrences,
  DsgvoReviewItemStatus,
} from "./api";

/**
 * Pure presentation logic of the DSGVO area ([F-024]): the journal's badges
 * and kinds, the labels of data objects and fields, which actions an entry or
 * a person allows, and how an elevation refusal is recognised. Kept free of
 * React so the rules are unit-testable.
 */

// i18n key maps. Callers resolve these via a variable (t(labelKey)); every
// value below exists in de.ts (journal.test.ts checks them against it).
export const JOURNAL_STATUS_LABEL_KEY: Record<DeletionJournalStatus, string> = {
  pending: "dsgvo.journal.status.pending",
  applied: "dsgvo.journal.status.applied",
  replayed: "dsgvo.journal.status.replayed",
  failed: "dsgvo.journal.status.failed",
};

export const JOURNAL_KIND_LABEL_KEY: Record<DeletionJournalKind, string> = {
  erasure: "dsgvo.journal.kind.erasure",
  disclosure: "dsgvo.journal.kind.disclosure",
};

export const DATA_OBJECT_LABEL_KEY: Record<DsgvoDataObject, string> = {
  incident: "dsgvo.dataObject.incident",
  timeline_event: "dsgvo.dataObject.timeline_event",
  attachment: "dsgvo.dataObject.attachment",
  postmortem: "dsgvo.dataObject.postmortem",
  followup_task: "dsgvo.dataObject.followup_task",
  operations_log: "dsgvo.dataObject.operations_log",
  input_draft: "dsgvo.dataObject.input_draft",
  audit_log: "dsgvo.dataObject.audit_log",
};

export const FIELD_LABEL_KEY: Record<DsgvoOccurrenceField, string> = {
  reported_by: "dsgvo.field.reported_by",
  owner: "dsgvo.field.owner",
  assignee: "dsgvo.field.assignee",
  author: "dsgvo.field.author",
  uploaded_by: "dsgvo.field.uploaded_by",
  removed_by: "dsgvo.field.removed_by",
  assigned_to: "dsgvo.field.assigned_to",
  recorded_by: "dsgvo.field.recorded_by",
  saved_by: "dsgvo.field.saved_by",
  actor: "dsgvo.field.actor",
  entity: "dsgvo.field.entity",
  title: "dsgvo.field.title",
  description: "dsgvo.field.description",
  detected_via: "dsgvo.field.detected_via",
  legal_hold_reason: "dsgvo.field.legal_hold_reason",
  text: "dsgvo.field.text",
  cause: "dsgvo.field.cause",
  affected_components: "dsgvo.field.affected_components",
  resolution: "dsgvo.field.resolution",
  prevention: "dsgvo.field.prevention",
  positive_learnings: "dsgvo.field.positive_learnings",
  earlier_detectable: "dsgvo.field.earlier_detectable",
  filename_or_url: "dsgvo.field.filename_or_url",
  removal_reason: "dsgvo.field.removal_reason",
  short_description: "dsgvo.field.short_description",
  old_value: "dsgvo.field.old_value",
  new_value: "dsgvo.field.new_value",
};

export const REVIEW_ITEM_STATUS_LABEL_KEY: Record<DsgvoReviewItemStatus, string> = {
  open: "dsgvo.review.itemStatus.open",
  edited: "dsgvo.review.itemStatus.edited",
  confirmed: "dsgvo.review.itemStatus.confirmed",
};

// Functional status colours, distinct from the Ryddion brand accent and never
// the only signal (the German label always shows too).
export const JOURNAL_STATUS_BADGE_CLASS: Record<DeletionJournalStatus, string> = {
  pending: "border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/40 text-slate-800 dark:text-slate-200",
  applied: "border-emerald-300 dark:border-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200",
  replayed: "border-sky-300 dark:border-sky-700 bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200",
  failed: "border-red-300 dark:border-red-700 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200",
};

/** Colour of the derived "Backup-Restfrist bis ..." badge (ADR-C-014). */
export const BACKUP_BADGE_CLASS = "border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100";

/** Colour of the "manuell zu prüfen" mark on a free-text occurrence. */
export const FREE_TEXT_BADGE_CLASS = "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100";

export interface JournalBadge {
  labelKey: string;
  className: string;
  /** ISO timestamp interpolated into the label (backup rest period only). */
  date?: string;
}

/**
 * Badges for one journal row: always the status badge; an APPLIED erasure
 * with a backup expiry additionally wears the "Backup-Restfrist bis
 * DD.MM.YYYY" badge (ADR-C-011/C-014 two-stage promise). An Auskunft has no
 * backup expiry.
 */
export function journalBadges(
  entry: Pick<DeletionJournalEntry, "status" | "backupExpiresAt">,
): JournalBadge[] {
  const badges: JournalBadge[] = [
    {
      labelKey: JOURNAL_STATUS_LABEL_KEY[entry.status],
      className: JOURNAL_STATUS_BADGE_CLASS[entry.status],
    },
  ];
  if (entry.status === "applied" && entry.backupExpiresAt) {
    badges.push({
      labelKey: "dsgvo.journal.backupUntil",
      className: BACKUP_BADGE_CLASS,
      date: entry.backupExpiresAt,
    });
  }
  return badges;
}

/** Latest lifecycle timestamp of an entry ("letzte Aktualisierung" column). */
export function lastUpdatedAt(
  entry: Pick<DeletionJournalEntry, "requestedAt" | "appliedAt" | "lastReplayAt" | "resolvedAt">,
): string {
  const candidates = [entry.requestedAt, entry.appliedAt, entry.lastReplayAt, entry.resolvedAt].filter(
    (iso): iso is string => iso !== null && iso !== undefined,
  );
  return candidates.reduce((latest, iso) => (iso > latest ? iso : latest));
}

/** The "Umfang" of an entry: occurrences over every data object. */
export function scopeTotal(entry: Pick<DeletionJournalEntry, "scope">): number {
  return Object.values(entry.scope).reduce((sum, n) => sum + n, 0);
}

/**
 * Two-stage confirmation: the destructive "Löschung beauftragen" button arms
 * only once the occurrences have loaded AND the auditor ticked the
 * confirmation, and stays disabled while the order is in flight.
 */
export function isOrderEnabled(confirmed: boolean, hasPreview: boolean, isPending: boolean): boolean {
  return confirmed && hasPreview && !isPending;
}

/**
 * May this person be erased from here? Only a person with an account
 * (a users row without one is an author, not a person), not yet anonymised,
 * and without an erasure already ordered - that one is finished from the
 * journal (replay) and the review.
 */
export function isErasable(
  person: Pick<DsgvoPerson, "hasAccount" | "anonymized">,
  occurrences: Pick<DsgvoPersonOccurrences, "erasureJournalId"> | undefined,
): boolean {
  return person.hasAccount && !person.anonymized && (occurrences?.erasureJournalId ?? null) === null;
}

/** Mandatory review-closing note: at least 10 characters after trim. */
export const MIN_REVIEW_NOTE_CHARS = 10;

export function isValidReviewNote(note: string): boolean {
  return note.trim().length >= MIN_REVIEW_NOTE_CHARS;
}

/** The "Manuelle Prüfung" queue: erasures whose review is still open. */
export function openReviews(entries: readonly DeletionJournalEntry[]): DeletionJournalEntry[] {
  return entries.filter((entry) => entry.kind === "erasure" && entry.manualReviewStatus === "open");
}

/** The review closes once every item is edited or confirmed ([NF-014]). */
export function canCloseReview(entry: Pick<DeletionJournalEntry, "reviewItemsOpen">): boolean {
  return entry.reviewItemsOpen === 0;
}

/** Replay applies to erasures that ran, or failed to run, their steps. */
export function canReplayEntry(entry: Pick<DeletionJournalEntry, "kind" | "status">): boolean {
  return entry.kind === "erasure" && (entry.status === "applied" || entry.status === "replayed" || entry.status === "failed");
}

/**
 * Did the server refuse because the session is not elevated ([F-033])? The
 * refusal is a 403 problem with `requires: "elevation"` - the same field the
 * account module's own refusals carry.
 */
export function needsElevation(error: unknown): boolean {
  const failure = toApiFailure(error);
  const problem = failure.problem as { requires?: unknown } | undefined;
  return failure.status === 403 && problem?.requires === "elevation";
}
